import { useApolloClient } from '@apollo/client'
import { useFragment } from '@apollo/client'
import {
  MarkUploadDocumentTaskAsDoneDocument,
  ProcurementValueObjectsUploadDocumentTaskTypeEnum,
  ProcurementsApprovalTimelineDocument,
  TaskActivityDocument,
  UploadDocumentTaskContentFragmentDoc,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import {
  TaskActionsHook,
  TaskCustomComponent,
  TaskDefinition,
} from 'modules/inbox_tasks/lib/abstraction'
import ProcurementPurchaseRequestTaskContent from 'modules/inbox_tasks/tasks/approval/ProcurementPurchaseRequest'
import { DownloadDocument } from 'modules/inbox_tasks/tasks/documentTask/downloadDocument'
import { UploadDocument } from 'modules/inbox_tasks/tasks/documentTask/uploadDocument'

type State = {
  uploadedFiles?: string[]
  downloadedFiles?: number[]
}

const getTaskType = (taskable?: {
  taskType?: 'upload' | 'download' | null
}): ProcurementValueObjectsUploadDocumentTaskTypeEnum | undefined => {
  const taskTypeValue = taskable && 'taskType' in taskable ? taskable['taskType'] : undefined
  return taskTypeValue === 'upload' || taskTypeValue === 'download' ? taskTypeValue : undefined
}

const DocumentTaskContent: TaskCustomComponent<'ProcurementUploadDocumentTask', State> = ({
  taskable,
  taskId,
}) => {
  const { data: taskData } = useFragment({
    fragment: UploadDocumentTaskContentFragmentDoc,
    fragmentName: 'UploadDocumentTaskContent',
    from: {
      __typename: 'TasksInboxTask',
      id: taskId,
    },
  })

  const purchaseRequest = taskable?.purchaseRequest
  const owner = purchaseRequest?.requesterEmployee?.employeeName ?? undefined

  const requesterContent = useEmployeeTableViewRow({ employee: owner, title: 'Requester' })

  if (!taskable) return null

  const taskType = getTaskType(taskable)
  const isUpload = taskType === 'upload'

  const detailsContent = taskType ? (
    isUpload ? (
      <UploadDocument
        content={taskData?.content}
        uploadDocumentTaskId={taskable.id}
        files={taskable?.files}
      />
    ) : (
      <DownloadDocument content={taskData?.content} files={taskable?.files} />
    )
  ) : null

  return (
    <ProcurementPurchaseRequestTaskContent
      id={purchaseRequest?.purchaseRequestId ?? '0'}
      requesterContent={requesterContent}
      detailsContent={detailsContent}
    />
  )
}

const useDocumentTaskActions: TaskActionsHook<'ProcurementUploadDocumentTask', State> = ({
  taskId,
  taskable,
  taskKind,
}) => {
  const apolloClient = useApolloClient()
  const { employee } = useCurrentSession()

  const [markAsDone, { loading: isMarkingAsDone }] = useMutation(
    MarkUploadDocumentTaskAsDoneDocument,
    {
      extractorFn: (data) => data.procurementMutations.markAsDoneProcurementUploadDocumentTask,
    }
  )

  const handleMarkAsDone = useUnsafeCallback(async () => {
    if (!taskable?.id || !employee?.id) {
      return false
    }

    const hasFiles = !!taskable.files?.length
    if (!hasFiles) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.errors.file_not_attached'
        ),
      })
      return false
    }

    const result = await markAsDone({
      variables: {
        uploadDocumentTaskId: taskable.id,
        completedById: employee.id,
      },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.success.mark_as_done'
        ),
      })

      apolloClient.refetchQueries({
        include: [TaskActivityDocument, ProcurementsApprovalTimelineDocument],
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.errors.mark_as_done_failed'
        ),
      })
    }
  }, [taskable, employee?.id, markAsDone, apolloClient])

  if (!taskable?.id) {
    return {}
  }

  const hasFiles = taskable.files && taskable.files.length > 0

  const disableMarkAsDone = !hasFiles || isMarkingAsDone

  return {
    onClickPrimaryAction: handleMarkAsDone,
    actionsDisabled: disableMarkAsDone,
  }
}

export const DocumentTaskDefinition: TaskDefinition<'ProcurementUploadDocumentTask', State> = {
  taskableTypename: 'ProcurementUploadDocumentTask',
  initialState: {},
  component: DocumentTaskContent,
  useActions: useDocumentTaskActions,
  getTitle: ({ info }) =>
    info.taskType === 'upload'
      ? i18n.t('tasks.types.procurement_request_document.upload_document.title', {
          taskName: info.purchaseRequest?.description ?? '',
        })
      : i18n.t('tasks.types.procurement_request_document.download_document.title', {
          taskName: info.purchaseRequest?.description ?? '',
        }),
  getMetadata: ({ info }) => [info.taskContent || ''],
}
